const SERVICE_KEY_NAMES: Record<string, string> = {
  verified_email_enrichment: "Email Enrichment",
  mobile_number_enrichment: "Phone Enrichment",
  email_enrichment: "Email Enrichment",
  phone_enrichment: "Phone Enrichment",
  us_call_minute: "Call Minutes",
  call_minute: "Call Minutes",
  call_minutes: "Call Minutes",
  call_recording_minute: "Call Recording",
  ai_draft: "AI Draft",
  ai_call_summary: "AI Call Summary",
  ai_content_generation: "AI Generation",
  ai_prospecting_agent: "AI Prospecting Agent",
  ai_insights: "AI & Insights",
  sms_outbound: "SMS Outbound",
  sms_inbound: "SMS Inbound",
  sms_segment: "SMS",
  mms_inbound: "MMS Inbound",
};

const CALL_MINUTE_KEYS = new Set([
  "us_call_minute",
  "call_recording_minute",
  "call_minute",
  "call_minutes",
]);
const INTEGER_KEYS = new Set([
  "sms_outbound",
  "sms_inbound",
  "mms_inbound",
  "sms_segment",
  "verified_email_enrichment",
  "email_enrichment",
  "mobile_number_enrichment",
  "phone_enrichment",
  "ai_prospecting_agent",
]);

export function formatServiceKey(key: string | null | undefined): string {
  if (!key) return "";
  return SERVICE_KEY_NAMES[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatUsageQuantity(serviceKey: string | null | undefined, units: number): string {
  const n = Number(units) || 0;

  if (serviceKey && CALL_MINUTE_KEYS.has(serviceKey)) {
    const totalSeconds = Math.max(0, Math.round(n * 60));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds}s`;
    if (seconds === 0) return `${minutes}m`;
    return `${minutes}m ${seconds}s`;
  }

  if (serviceKey && INTEGER_KEYS.has(serviceKey)) {
    return String(Math.round(n));
  }

  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2);
}

export interface AgentModelUsage {
  model: string;
  name: string;
  icon: string;
  color: string;
  inputTokens: number;
  outputTokens: number;
  /** This model's share of the event's tokens, 0–1. */
  share: number;
}

export interface AgentUsage {
  models: AgentModelUsage[];
  inputTokens: number;
  outputTokens: number;
}

const MODEL_BRANDS: Array<{ match: RegExp; icon: string; color: string }> = [
  { match: /gemini/i, icon: "simple-icons:googlegemini", color: "#4f7cf7" },
  { match: /claude/i, icon: "simple-icons:claude", color: "#d97757" },
  { match: /gpt|openai/i, icon: "simple-icons:openai", color: "#10a37f" },
];
const DEFAULT_MODEL_BRAND = { icon: "mdi:creation", color: "#7c3aed" };

/**
 * "google/gemini-2.5-flash-lite" → "Gemini 2.5 Flash Lite",
 * "claude-sonnet-4-5-20250929" → "Claude Sonnet 4.5" (date dropped, version parts joined).
 */
const formatModelName = (model: string): string =>
  model
    .replace(/^[^/]+\//, "")
    .replace(/[-_@]\d{8}$/, "")
    .split(/[-_]/)
    .filter(Boolean)
    .reduce<string[]>((parts, part) => {
      const prev = parts[parts.length - 1];
      if (/^\d+$/.test(part) && prev && /^\d+(\.\d+)*$/.test(prev)) {
        parts[parts.length - 1] = `${prev}.${part}`;
      } else {
        parts.push(/^[a-z]/.test(part) ? part[0].toUpperCase() + part.slice(1) : part);
      }
      return parts;
    }, [])
    .join(" ");

/** One decimal below 10, none above, so 9_960 is "10K" rather than "10.0K". */
const compactTokens = (v: number): string => v.toFixed(Number(v.toFixed(1)) >= 10 ? 0 : 1);

/** 112044 → "112K", 1534 → "1.5K", 999999 → "1.0M", 97 → "97". */
export function formatTokenCount(n: number): string {
  if (n >= 999_500) return `${compactTokens(n / 1_000_000)}M`;
  if (n >= 1_000) return `${compactTokens(n / 1_000)}K`;
  return String(n);
}

/**
 * The models and tokens behind an AI Prospecting Agent charge, for its hover card.
 * Null for other usage types, or a row recorded without token details.
 */
export function agentUsageFrom(
  serviceKey: string | null | undefined,
  metadata: Record<string, any> | null | undefined
): AgentUsage | null {
  const usage = serviceKey === "ai_prospecting_agent" ? metadata?.usage : null;
  if (!usage) return null;

  const inputTokens = Number(usage.inputTokens) || 0;
  const outputTokens = Number(usage.outputTokens) || 0;
  const total = inputTokens + outputTokens;
  if (total === 0) return null;

  const models: AgentModelUsage[] = (Array.isArray(usage.models) ? usage.models : []).map(
    (m: any) => {
      const model = String(m?.model ?? "unknown");
      const modelIn = Number(m?.inputTokens) || 0;
      const modelOut = Number(m?.outputTokens) || 0;
      const brand = MODEL_BRANDS.find((b) => b.match.test(model)) ?? DEFAULT_MODEL_BRAND;
      return {
        model,
        name: formatModelName(model),
        icon: brand.icon,
        color: brand.color,
        inputTokens: modelIn,
        outputTokens: modelOut,
        // Capped so per-model counts that overshoot the event total can't overflow the bar.
        share: Math.min((modelIn + modelOut) / total, 1),
      };
    }
  );

  return { models: models.sort((a, b) => b.share - a.share), inputTokens, outputTokens };
}
